/**
 * Editor-only preview guard + controller boot.
 *
 * Wraps a block's ServerSideRender output and:
 *   1. Neutralizes navigation inside the block editor.
 *   2. Boots carousel / map controllers when SSR markup appears.
 *
 * Retry budget is absolute while a map/carousel stays pending: the
 * MutationObserver must not reset attempts (Leaflet mutates the canvas
 * heavily). After a successful ready state, attempts reset so a later SSR
 * refresh can boot again.
 *
 * @package
 * @since   3.5.0
 */

import { useRef, useEffect } from '@wordpress/element';

const MAX_BOOT_ATTEMPTS = 20;
const BOOT_INTERVAL_MS = 100;

/**
 * @param {HTMLElement|null} node Preview root.
 */
function bootControllers( node ) {
	if ( ! node || typeof window === 'undefined' ) {
		return;
	}

	// Iframed editor (WP 6.3+): the SSR markup lives in the iframe document,
	// and enqueue_block_assets loads the controllers into BOTH windows. Prefer
	// the copy that shares the node's document — its `document`/`L` references
	// point at the right DOM. Fall back to the parent copy (non-iframed
	// editors) which resolves the node's ownerDocument internally.
	const frameWin = node.ownerDocument
		? node.ownerDocument.defaultView
		: null;
	const wins = frameWin && frameWin !== window ? [ frameWin, window ] : [ window ];

	for ( const win of wins ) {
		if ( typeof win.hvnlyInitBlockCarousels === 'function' ) {
			win.hvnlyInitBlockCarousels( node );
		}
		if ( typeof win.hvnlyInitBlockMaps === 'function' ) {
			win.hvnlyInitBlockMaps( node );
		}
	}
}

/**
 * @param {HTMLElement} node Preview root.
 * @return {boolean} Whether map/carousel markup still needs booting.
 */
function hasPendingControllers( node ) {
	return !!(
		node.querySelector(
			'[data-hvnly-block-map]:not([data-hvnly-block-map-ready="1"])'
		) ||
		node.querySelector(
			'[data-hvnly-block-carousel]:not([data-hvnly-block-carousel-ready="1"])'
		)
	);
}

/**
 * @param {Object}  props
 * @param {Element} props.children The preview content (e.g. ServerSideRender).
 * @return {Element} Wrapped, interaction-guarded preview.
 */
export default function EditorPreview( { children } ) {
	const ref = useRef( null );

	useEffect( () => {
		const node = ref.current;

		if ( ! node ) {
			return undefined;
		}

		const preventNavigation = ( event ) => {
			const interactive =
				event.target && event.target.closest
					? event.target.closest(
							'a[href], button, input[type="submit"], input[type="button"], [role="button"]'
					  )
					: null;

			if ( interactive ) {
				event.preventDefault();
			}
		};

		const preventSubmit = ( event ) => event.preventDefault();

		node.addEventListener( 'click', preventNavigation, true );
		node.addEventListener( 'submit', preventSubmit, true );

		let bootTimer = null;
		let attempts = 0;
		let stopped = false;
		let observer = null;

		const stopRetries = () => {
			stopped = true;
			window.clearTimeout( bootTimer );
			bootTimer = null;
			if ( observer ) {
				observer.disconnect();
			}
		};

		const scheduleBoot = () => {
			if ( stopped ) {
				return;
			}

			window.clearTimeout( bootTimer );
			bootTimer = window.setTimeout( () => {
				bootControllers( node );

				if ( ! hasPendingControllers( node ) ) {
					// Ready (or no controllers). Allow a later SSR refresh to retry.
					attempts = 0;
					return;
				}

				attempts += 1;
				if ( attempts >= MAX_BOOT_ATTEMPTS ) {
					stopRetries();
					return;
				}

				scheduleBoot();
			}, BOOT_INTERVAL_MS );
		};

		scheduleBoot();

		observer = new window.MutationObserver( () => {
			if ( stopped || ! hasPendingControllers( node ) ) {
				return;
			}
			// Never reset attempts while still pending — that caused endless loops.
			scheduleBoot();
		} );
		observer.observe( node, { childList: true, subtree: true } );

		return () => {
			stopRetries();
			node.removeEventListener( 'click', preventNavigation, true );
			node.removeEventListener( 'submit', preventSubmit, true );
		};
	}, [] );

	return (
		<div ref={ ref } className="hvnly-block-editor-preview">
			{ children }
		</div>
	);
}
